import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import Select from "react-select";
import axios from "axios";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { useNavigate, Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { toast } from "react-toastify";
import { validateEmail } from "../utils/passwordValidation";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const calculateAge = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const SetViewOnClick = ({ coords, shouldPan }) => {
  const map = useMap();
  useEffect(() => {
    if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
      map.setView(coords, 13);
    }
  }, [coords, map, shouldPan]);
  return null;
};

const MapEvents = ({ onMapClick }) => {
  useMapEvents({
    click: onMapClick,
  });
  return null;
};

// Validation functions
const validateContactNumber = (contact) => {
  if (!contact) return "Contact number is required";
  const contactRegex = /^0\d{10}$/;
  if (!contactRegex.test(contact)) {
    return "Contact number must start with 0 and be exactly 11 digits (e.g., 03XX-XXXXXXX)";
  }
  return "";
};

const validateRequiredField = (value, fieldName) => {
  if (!value || value.trim() === "") {
    return `${fieldName} is required`;
  }
  return "";
};

const Signup = () => {
  const [userInfo, setUserInfo] = useState({
    user_name: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    gender: "",
    contact: "",
    dob: "",
    house_number: "",
    street_number: "",
    area: "",
    city: "",
    country: "",
    coordinates: "",
    password: "",
    re_password: "",
    emergency_contact: "",
    sports: [],
    picture: null,
    age: "",
    is_profile_private: false,
  });

  const [sportsOptions, setSportsOptions] = useState([]);
  const [mapCenter, setMapCenter] = useState([33.6844, 73.0479]); // Islamabad coordinates
  const [marker, setMarker] = useState(null);
  const [shouldPan, setShouldPan] = useState(false);
  const [isTester, setIsTester] = useState(false);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  // Validation errors state
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchSportsOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/register/get-sports`
        );
        const filteredSports = response.data.filter(
          (sport) => sport.value !== "General"
        );
        const formattedOptions = filteredSports.map((sport) => ({
          value: sport.value,
          label: sport.value,
        }));
        setSportsOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching sports options:", error);
      }
    };

    const fetchVerifiedEmail = async () => {
      try {
        // Get email and tester flag from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const emailFromUrl = urlParams.get("email");
        const testerFromUrl = urlParams.get("tester");

        if (emailFromUrl) {
          // Verify this email is actually verified in the database
          const response = await axios.get(
            `${
              process.env.REACT_APP_BASE_URL
            }/api/register/get-verified-email?email=${encodeURIComponent(
              emailFromUrl
            )}`
          );
          if (response.data.success) {
            setUserInfo((prevInfo) => ({
              ...prevInfo,
              email: response.data.email,
            }));

            // Set tester status based on URL parameter or database response
            setIsTester(testerFromUrl === "true" || response.data.isTester);
          }
        } else {
          // No email in URL, redirect to verification page or show error
          console.warn(
            "No email parameter found in URL. User may have accessed signup page directly."
          );
          // Optionally redirect to verification page
          // navigate('/verification');
        }
      } catch (error) {
        console.error("Error fetching verified email:", error);
        // If email verification fails, user might need to go through verification again
        if (error.response && error.response.status === 404) {
          console.warn(
            "Email not found or not verified. Redirecting to verification page."
          );
          // Optionally redirect to verification page
          // navigate('/verification');
        }
      }
    };

    fetchSportsOptions();
    fetchVerifiedEmail();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "dob") {
      const age = calculateAge(value);
      setUserInfo({ ...userInfo, [name]: value, age });
    } else {
      setUserInfo({ ...userInfo, [name]: value });
    }
  };

  const handleSportsChange = (selectedOptions) => {
    const selectedSports = selectedOptions.map((option) => option.value);
    setUserInfo({ ...userInfo, sports: selectedSports });
  };

  const handlePictureChange = (acceptedFiles) => {
    setUserInfo({ ...userInfo, picture: acceptedFiles[0] });
  };

  const geocodeAddress = useCallback(async () => {
    const { country, city, area, street_number, house_number } = userInfo;
    const address =
      `${house_number} ${street_number}, ${area}, ${city}, ${country}`.trim();
    if (!address) return;

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: address,
            format: "json",
            limit: 1,
            addressdetails: 1,
          },
        }
      );

      if (response.data.length > 0) {
        const { lat, lon, address: details } = response.data[0];
        setMapCenter([parseFloat(lat), parseFloat(lon)]);
        setMarker([parseFloat(lat), parseFloat(lon)]);
        setShouldPan(true);
        setUserInfo((prevData) => ({
          ...prevData,
          country: details.country || prevData.country,
          city: details.city || details.town || prevData.city,
          area: details.suburb || prevData.area,
          street_number: details.road || prevData.street_number,
          house_number: details.house_number || prevData.house_number,
          coordinates: `${lat}, ${lon}`,
        }));
      }
    } catch (error) {
      console.error("Error geocoding:", error);
    }
  }, [userInfo]);

  const handleMapClick = useCallback(async (e) => {
    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setMapCenter([lat, lng]);
    setShouldPan(true);

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: {
            lat: lat,
            lon: lng,
            format: "json",
            addressdetails: 1,
          },
        }
      );

      if (response.data) {
        const { address } = response.data;
        setUserInfo((prevData) => ({
          ...prevData,
          country: address.country || "",
          city: address.city || address.town || "",
          area: address.suburb || address.neighbourhood || address.county || "",
          street_number: address.road || "",
          house_number: address.house_number || "",
          coordinates: `${lat}, ${lng}`,
        }));
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const newErrors = {};

    // Required fields validation
    newErrors.user_name = validateRequiredField(
      userInfo.user_name,
      "User Name"
    );
    newErrors.first_name = validateRequiredField(
      userInfo.first_name,
      "First Name"
    );
    newErrors.last_name = validateRequiredField(
      userInfo.last_name,
      "Last Name"
    );
    newErrors.contact = validateContactNumber(userInfo.contact);
    newErrors.dob = validateRequiredField(userInfo.dob, "Date of Birth");
    newErrors.street_number = validateRequiredField(
      userInfo.street_number,
      "Street Number"
    );
    newErrors.area = validateRequiredField(userInfo.area, "Area");
    newErrors.city = validateRequiredField(userInfo.city, "City");
    newErrors.country = validateRequiredField(userInfo.country, "Country");
    newErrors.sports =
      userInfo.sports.length === 0 ? "Please select at least one sport" : "";
    newErrors.gender = validateRequiredField(userInfo.gender, "Gender");

    // Email validation (if not pre-filled)
    if (userInfo.email) {
      const emailError = validateEmail(userInfo.email);
      if (emailError) newErrors.email = emailError;
    }

    setErrors(newErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      toast.error("Please fix the errors in the form before submitting.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("userInfo", JSON.stringify(userInfo));
      if (userInfo.picture) {
        formData.append("userPicture", userInfo.picture);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/register/signup-registration`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log(response.data);

      if (response.data.success) {
        const successMessage = isTester
          ? "Tester registration successful!"
          : "Welcome to D Sports Hub!";
        toast.success(successMessage);
        navigate("/");
      } else {
        toast.error(`${response.data.error}`);
      }
    } catch (error) {
      console.error("Error during signup:", error);

      // Show user-friendly error message
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(`${error.response.data.error}`);
      } else {
        toast.error("An error occurred during registration. Please try again.");
      }
    }
  };

  const { getRootProps: getUserRootProps, getInputProps: getUserInputProps } =
    useDropzone({ onDrop: handlePictureChange });

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-[60px] mx-auto mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => navigate(-1)}
          />
          <h1 className="text-3xl font-bold text-[#3498db] mb-2">Sign Up</h1>
          <p className="text-[#57606f] mt-2">
            Create an account by filling the form below!
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-8 space-y-8 backdrop-blur-[10px] border border-white/20"
        >
          {/* Profile Photo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Photo <span className="text-gray-500">(Optional)</span>
            </label>
            <div
              {...getUserRootProps()}
              className="flex items-center justify-center w-full"
            >
              <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-gray-300 border-dashed rounded-full cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                <input {...getUserInputProps()} />
                {userInfo.picture ? (
                  <img
                    src={URL.createObjectURL(userInfo.picture)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <i className="fas fa-camera text-gray-500 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-500">Upload Photo</p>
                  </div>
                )}
              </label>
            </div>
          </div>
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="user_name"
                  value={userInfo.user_name}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.user_name
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter username"
                />
                {errors.user_name && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.user_name}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={userInfo.first_name}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.first_name
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter first name"
                />
                {errors.first_name && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.first_name}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Middle Name <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="middle_name"
                  value={userInfo.middle_name}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter middle name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={userInfo.last_name}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.last_name
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter last name"
                />
                {errors.last_name && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.last_name}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email{" "}
                  {userInfo.email === "" && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="email"
                  name="email"
                  value={userInfo.email}
                  onChange={handleInputChange}
                  readOnly={userInfo.email !== ""}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.email
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : userInfo.email !== ""
                      ? "border-[#ddd] bg-[#f8f9fa]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  style={{
                    cursor: userInfo.email !== "" ? "not-allowed" : "text",
                  }}
                  placeholder="Enter email"
                  required
                />
                {errors.email && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.email}
                  </div>
                )}
                {userInfo.email !== "" && (
                  <p className="mt-1 text-xs text-gray-500">
                    This email was verified and cannot be changed
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  onChange={handleInputChange}
                  required
                  className="mr-2"
                />
                <span>Male</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  onChange={handleInputChange}
                  required
                  className="mr-2"
                />
                <span>Female</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="Other"
                  onChange={handleInputChange}
                  required
                  className="mr-2"
                />
                <span>Other</span>
              </label>
            </div>
            {errors.gender && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {errors.gender}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="contact"
                value={userInfo.contact}
                onChange={handleInputChange}
                required
                className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                  errors.contact
                    ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                    : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                }`}
                placeholder="03XX-XXXXXXX"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter your mobile number starting with 0 (11 digits total)
              </p>
              {errors.contact && (
                <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                  <span className="text-xs">⚠</span>
                  {errors.contact}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="dob"
                value={userInfo.dob}
                onChange={handleInputChange}
                required
                className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                  errors.dob
                    ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                    : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                }`}
              />
              {errors.dob && (
                <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                  <span className="text-xs">⚠</span>
                  {errors.dob}
                </div>
              )}
            </div>
          </div>
          {/* Address Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Address Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House Number <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="house_number"
                  value={userInfo.house_number}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter house number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="street_number"
                  value={userInfo.street_number}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.street_number
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter street number"
                />
                {errors.street_number && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.street_number}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="area"
                  value={userInfo.area}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.area
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter area"
                />
                {errors.area && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.area}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={userInfo.city}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.city
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.city}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  value={userInfo.country}
                  onChange={handleInputChange}
                  required
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.country
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter country"
                />
                {errors.country && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.country}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coordinates
                </label>
                <input
                  type="text"
                  name="coordinates"
                  value={userInfo.coordinates}
                  readOnly
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa]"
                  placeholder="Click on map or find coordinates"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={geocodeAddress}
              className="mb-4 px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] focus:outline-none transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
            >
              Find Coordinates from Address
            </button>
            <div className="h-96 w-full rounded-lg overflow-hidden border border-gray-300">
              <MapContainer
                center={mapCenter}
                zoom={11}
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {marker && <Marker position={marker} />}
                <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
                <MapEvents onMapClick={handleMapClick} />
              </MapContainer>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Contact{" "}
                <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                name="emergency_contact"
                value={userInfo.emergency_contact}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                placeholder="Enter emergency contact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sports Interested In <span className="text-red-500">*</span>
              </label>
              <Select
                isMulti
                name="sports"
                options={sportsOptions}
                className={`basic-multi-select ${
                  errors.sports ? "border-red-300" : ""
                }`}
                classNamePrefix="select"
                value={sportsOptions.filter((option) =>
                  userInfo.sports.includes(option.value)
                )}
                onChange={handleSportsChange}
                placeholder="Select sports..."
              />
              {errors.sports && (
                <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                  <span className="text-xs">⚠</span>
                  {errors.sports}
                </div>
              )}
            </div>
          </div>
          {/* Profile Privacy */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Privacy
            </label>
            <div className="space-y-3">
              <label className="flex items-start p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="is_profile_private"
                  value={false}
                  checked={!userInfo.is_profile_private}
                  onChange={() =>
                    setUserInfo({ ...userInfo, is_profile_private: false })
                  }
                  className="mt-1 mr-3"
                />
                <span className="text-sm text-gray-700">
                  Public Profile (Other users can see your profile without
                  personal info)
                </span>
              </label>
              <label className="flex items-start p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="is_profile_private"
                  value={true}
                  checked={userInfo.is_profile_private}
                  onChange={() =>
                    setUserInfo({ ...userInfo, is_profile_private: true })
                  }
                  className="mt-1 mr-3"
                />
                <span className="text-sm text-gray-700">
                  Private Profile (Only you can see your profile)
                </span>
              </label>
            </div>
          </div>

          {/* Terms Agreement */}
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="agreeTerms"
                required
                className="mr-3 w-5 h-5"
              />
              <span className="text-sm text-gray-700">
                Agree to the terms and responsible conduct.
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              className="w-full h-11 flex justify-center items-center border border-transparent rounded-lg text-base font-semibold text-white bg-[#3498db] hover:bg-[#2980b9] focus:outline-none transition-all duration-300 uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
            >
              {isTester ? "Create Tester Account" : "Create Account"}
            </button>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Do you already have an account?{" "}
                <Link
                  to="/login-arena"
                  className="text-[#3498db] hover:underline font-semibold"
                >
                  LOGIN
                </Link>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
